import React, { useState, useEffect } from 'react';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { FormConfiguration, FormQuestion } from '../types';
import QuestionEditor from './QuestionEditor';

const FormEditor: React.FC = () => {
  const [forms, setForms] = useState<FormConfiguration[]>([]);
  const [selectedForm, setSelectedForm] = useState<FormConfiguration | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const formsRef = collection(db, 'formConfigurations');
      const snapshot = await getDocs(formsRef);
      const formsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FormConfiguration));
      setForms(formsData);
      if (formsData.length > 0) {
        // Automatically select the first form
        setSelectedForm(formsData[0]);
      }
    } catch (error) {
      console.error("Error fetching form configurations:", error);
      setMessage('🔥 Error fetching forms.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const form = forms.find(f => f.id === e.target.value);
    setSelectedForm(form || null);
  };
  
  const handleQuestionChange = (index: number, updatedQuestion: FormQuestion) => {
    if (!selectedForm) return;
    const newQuestions = [...selectedForm.questions];
    newQuestions[index] = updatedQuestion;
    setSelectedForm({ ...selectedForm, questions: newQuestions });
  };

  const handleAddQuestion = () => {
    if (!selectedForm) return;
    const newQuestion: FormQuestion = {
      id: `new_question_${Date.now()}`,
      label: 'New Question',
      type: 'text',
      required: false,
    };
    setSelectedForm({
      ...selectedForm,
      questions: [...selectedForm.questions, newQuestion],
    });
  };

  const handleDeleteQuestion = async (index: number) => {
    if (!selectedForm) return;
    if (await window.appConfirm('Are you sure you want to delete this question?')) {
      const newQuestions = selectedForm.questions.filter((_, i) => i !== index);
      setSelectedForm({ ...selectedForm, questions: newQuestions });
    }
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    if (!selectedForm) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedForm.questions.length) return;

    const newQuestions = [...selectedForm.questions];
    const [movedQuestion] = newQuestions.splice(index, 1);
    newQuestions.splice(newIndex, 0, movedQuestion);

    setSelectedForm({ ...selectedForm, questions: newQuestions });
  };

  const handleSaveChanges = async () => {
    if (!selectedForm) return;
    setLoading(true);
    setMessage('');
    try {
      const formRef = doc(db, 'formConfigurations', selectedForm.id);
      // We need to strip the `id` from the top-level before saving
      const { id, ...formDataToSave } = selectedForm;
      await setDoc(formRef, formDataToSave);
      setMessage('✅ Form saved successfully!');
    } catch (error) {
      console.error("Error saving form:", error);
      setMessage('🔥 Error saving form.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !selectedForm) {
    return <div>Loading forms...</div>;
  }

  return (
    <div className="form-editor-container">
      <h2>Feedback Form Editor</h2>
      
      <div className="form-selector">
        <label htmlFor="form-version-selector">Select Form Version:</label>
        <select id="form-version-selector" onChange={handleFormChange} value={selectedForm?.id || ''}>
          {forms.map(form => (
            <option key={form.id} value={form.id}>{form.name} ({form.id})</option>
          ))}
        </select>
      </div>

      {selectedForm && (
        <>
          <div className="question-list">
            <h3>Questions for "{selectedForm.name}"</h3>
            {selectedForm.questions.map((question, index) => (
              <QuestionEditor
                key={question.id}
                index={index}
                question={question}
                onQuestionChange={handleQuestionChange}
                onDelete={handleDeleteQuestion}
                onMove={handleMoveQuestion}
                isFirst={index === 0}
                isLast={index === selectedForm.questions.length - 1}
              />
            ))}
          </div>
          <div className="form-actions">
            <button onClick={handleAddQuestion} className="add-question-btn">
              + Add Question
            </button>
            <button onClick={handleSaveChanges} disabled={loading} className="submit-button">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}
      
      {message && <div className={`seed-message ${message.includes('🔥') ? 'error' : 'success'}`}>{message}</div>}
    </div>
  );
};

export default FormEditor;
